import {useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {PLAY_SURFACE} from '#/components/capsules/play/playSurface'
import {QuestionHeading} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayAccent,
  usePlayContentPadding,
  usePlayFooterStyle,
  usePlayOptionRowRhythm,
  usePlayOptionTextStyle,
  usePlayPrimaryAction,
  usePlaySelectionMode,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {useFlashBookInkSound} from '#/components/flashbooks/useFlashBookInkSound'
import {SLIDE_TYPE_META} from '#/types/capsule'

const ACCENT = SLIDE_ACCENT.ordering

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export function OrderingSlidePlay({
  slide,
  onAnswer,
  showFeedback,
  primaryColor,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const palette = useFlashBookPlayPalette()
  const padding = usePlayContentPadding(24)
  // The items wear the book's own hand, not the app's UI font.
  const answerText = usePlayOptionTextStyle()
  // On ruled paper an item is not a bordered card: it is a blank rule with the
  // item written on the line under it, exactly like the answers to a question.
  // That is what keeps a list of them in register with the paper.
  const rowRhythm = usePlayOptionRowRhythm()
  // A whole number of the page's rules between the question and the items.
  const stackGap = usePlayStackGap(16)
  // Inside a book every interactive affordance wears the book's own accent.
  const accent = usePlayAccent(ACCENT)
  // The action strip is page furniture: on ruled paper it is the button alone,
  // printed on the lines, a whole number of rules tall.
  const footerStyle = usePlayFooterStyle()
  // The action, in the page's own language: on paper it is ink, not a button.
  const action = usePlayPrimaryAction(49)
  // Where the theme marks with a highlighter, a placed step is struck through
  // with the marker rather than dropped into a tinted row.
  const highlight = usePlaySelectionMode() === 'highlight'
  const ink = useInkStroke()
  const inkSound = useFlashBookInkSound()
  const orderItems = slide.orderItems ?? []
  const correctOrder = slide.correctOrder ?? []

  const [shuffled] = useState(() => shuffle(orderItems))
  const [userOrder, setUserOrder] = useState<string[]>([])
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)

  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setUserOrder([])
  }

  const unselected = shuffled.filter(item => !userOrder.includes(item.id))

  const handleSelectItem = (itemId: string) => {
    if (showFeedback) return
    // Placing a step is a marking gesture, so it sounds like one.
    inkSound.mark()
    setUserOrder(prev => [...prev, itemId])
  }

  const handleDeselectItem = (itemId: string) => {
    if (showFeedback) return
    inkSound.mark()
    setUserOrder(prev => prev.filter(id => id !== itemId))
  }

  /** Whether every step has a place yet — the action is dead until then. */
  const placedEveryStep = userOrder.length === orderItems.length

  const handleCheck = () => {
    const maxPoints = orderItems.length
    const points = userOrder.filter(
      (id, idx) => correctOrder[idx] === id,
    ).length
    onAnswer({
      isCorrect: points === maxPoints,
      points,
      maxPoints,
      solutionText: correctOrder
        .map(id => orderItems.find(i => i.id === id)?.label ?? '')
        .join(' → '),
      explanation: slide.explanation,
      userAnswer: userOrder,
    })
  }

  const getOrderedItemStyle = (id: string, idx: number) => {
    const isCorrect = showFeedback && correctOrder[idx] === id
    if (showFeedback) {
      return [
        ss.orderedItem,
        highlight
          ? null
          : {
              borderColor: isCorrect ? palette.positive : palette.negative,
              backgroundColor:
                (isCorrect ? palette.positive : palette.negative) + '18',
            },
      ]
    }
    return [
      ss.orderedItem,
      highlight
        ? null
        : {borderColor: accent + '66', backgroundColor: accent + '18'},
    ]
  }

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={[ss.scrollContent, {padding, gap: stackGap}]}>
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>

        {/* User-built order */}
        <View style={ss.section}>
          <Text style={[ss.sectionLabel, {color: palette.muted}]}>
            <Trans>Your order</Trans>
          </Text>
          <View style={ss.orderedList}>
            {userOrder.map((id, idx) => {
              const item = orderItems.find(i => i.id === id)
              if (!item) return null
              return (
                <TouchableOpacity
                  key={id}
                  style={[getOrderedItemStyle(id, idx), rowRhythm]}
                  onPress={() => handleDeselectItem(id)}
                  accessibilityRole="button"
                  accessibilityLabel={item.label}
                  accessibilityHint={undefined}>
                  <Text style={[ss.posNum, {color: accent}]}>{idx + 1}</Text>
                  <Text
                    style={[ss.itemText, answerText, {color: palette.body}]}>
                    <Text
                      style={ink(
                        showFeedback
                          ? correctOrder[idx] === id
                            ? palette.positive
                            : palette.negative
                          : accent,
                      )}>
                      {item.label}
                    </Text>
                  </Text>
                  {!showFeedback && (
                    <Feather name="x" size={16} color={palette.muted} />
                  )}
                </TouchableOpacity>
              )
            })}
            {userOrder.length === 0 && (
              <View
                style={[
                  ss.emptyPlaceholder,
                  {borderColor: palette.border},
                  rowRhythm,
                ]}>
                <Text
                  style={[ss.emptyText, answerText, {color: palette.muted}]}>
                  <Trans>Tap items below to build your order</Trans>
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Remaining items to pick */}
        {!showFeedback && unselected.length > 0 && (
          <View style={ss.section}>
            <Text style={[ss.sectionLabel, {color: palette.muted}]}>
              <Trans>Items</Trans>
            </Text>
            <View style={ss.bankList}>
              {unselected.map(item => (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    ss.bankItem,
                    highlight
                      ? null
                      : {
                          borderColor: palette.border,
                          backgroundColor: palette.surface,
                        },
                    rowRhythm,
                  ]}
                  onPress={() => handleSelectItem(item.id)}
                  accessibilityRole="button"
                  accessibilityLabel={item.label}
                  accessibilityHint={undefined}>
                  <Text
                    style={[ss.itemText, answerText, {color: palette.body}]}>
                    {item.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}
      </PlayScroller>

      {!showFeedback && (
        <View
          style={[ss.footer, {borderTopColor: palette.border}, footerStyle]}>
          <TouchableOpacity
            style={[
              ss.checkBtn,
              {
                backgroundColor: placedEveryStep
                  ? primaryColor
                  : palette.surfaceAlt,
              },
              action.style,
            ]}
            onPress={handleCheck}
            disabled={!placedEveryStep}
            accessibilityRole="button"
            accessibilityLabel={l`Check answer`}
            accessibilityHint={undefined}>
            <Text
              style={[
                ss.checkBtnText,
                {
                  color:
                    action.ink(placedEveryStep) ??
                    (placedEveryStep ? palette.onAccent : palette.muted),
                },
                action.labelStyle(placedEveryStep),
              ]}>
              <Trans>Check</Trans>
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  scrollContent: {padding: 24, gap: 16},
  question: {
    fontSize: 20,
    fontWeight: '700',
    color: PLAY_SURFACE.text,
    lineHeight: 28,
    marginTop: 8,
  },
  section: {gap: 8},
  sectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: PLAY_SURFACE.textFaint,
    letterSpacing: 0.5,
  },
  orderedList: {gap: 8},
  orderedItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1.5,
  },
  posNum: {
    fontSize: 14,
    fontWeight: '700',
    minWidth: 20,
    textAlign: 'center',
  },
  itemText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: PLAY_SURFACE.text,
  },
  bankList: {gap: 8},
  bankItem: {
    padding: 14,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: PLAY_SURFACE.border,
    backgroundColor: PLAY_SURFACE.surface,
  },
  emptyPlaceholder: {
    padding: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PLAY_SURFACE.border,
    borderStyle: 'dashed',
    alignItems: 'center',
  },
  emptyText: {fontSize: 13, color: PLAY_SURFACE.textFaint},
  explanationBox: {
    padding: 14,
    backgroundColor: PLAY_SURFACE.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PLAY_SURFACE.border,
  },
  explanationText: {
    fontSize: 14,
    color: PLAY_SURFACE.textMuted,
    lineHeight: 20,
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: PLAY_SURFACE.border,
  },
  checkBtn: {borderRadius: 14, paddingVertical: 15, alignItems: 'center'},
  checkBtnText: {color: '#fff', fontSize: 16, fontWeight: '700'},
  checkBtnDisabled: {color: 'rgba(255,255,255,0.4)'},
})
